import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && window.isSecureContext;
const toKey = (v) => { const pad = "=".repeat((4 - (v.length % 4)) % 4); const raw = atob((v + pad).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };

export const PushBell = ({ email, orderNumber, admin = false, compact = false }) => {
  const [state, setState] = useState("idle");
  const [ok, setOk] = useState(supported());

  useEffect(() => {
    if (!supported()) { setOk(false); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => { if (s) setState("on"); }).catch(() => {});
  }, []);

  const enable = async () => {
    setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); toast.error("Notifications were blocked in your browser"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data: cfg } = await api.get("/push/config");
      if (!cfg.enabled) throw new Error("Push not configured");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(cfg.publicKey) });
      await api.post("/push/subscribe", { ...sub.toJSON(), email: email || undefined, order_number: orderNumber || undefined });
      setState("on"); toast.success(admin ? "You'll get alerts for new paid orders on this device" : "We'll ping this device when your order is ready");
    } catch (err) { setState("idle"); toast.error(apiError(err.response?.data?.detail) || err.message || "Could not enable notifications"); }
  };
  const disable = async () => {
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setState("idle"); toast.success("Notifications off for this device");
    } catch { setState("on"); toast.error("Could not turn off"); }
  };

  if (!ok) return null;
  const on = state === "on";
  const label = state === "denied" ? "Notifications blocked" : on ? (compact ? "Alerts on" : "Alerts on — tap to turn off") : (compact ? "Notify me" : "Notify me on this device");
  const Icon = state === "denied" ? BellOff : on ? BellRing : Bell;
  return (
    <button type="button" onClick={on ? disable : enable} disabled={state === "busy" || state === "denied"} data-testid={admin ? "admin-push-bell" : "push-bell"}
      title={admin ? "Phone alerts for new paid orders & draft approvals" : "Get a phone alert when your order is ready for collection"}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-50 ${on ? "bg-[#10b981]/15 text-[#10b981] border-[#10b981]/40 hover:bg-[#10b981]/25" : "bg-[#1e222d] text-zinc-300 border-zinc-700 hover:border-[#ff6a13] hover:text-white"}`}>
      <Icon size={14} className={on ? "animate-pulse" : ""} />{state === "busy" ? "…" : label}
    </button>
  );
};
